import { inject } from "@angular/core";
import { CanActivateChildFn, CanActivateFn, Router } from "@angular/router";
import { AuthService } from "../auth/interceptors/auth.service";
import { AuthUtils } from "../auth/interceptors/auth.utils";
import { MsalBroadcastService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";
import { UserService } from "app/services/user.service";
import { catchError, filter, interval, map, of, switchMap, take } from "rxjs";

export const AuthGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
  const router: Router = inject(Router);
  const userService: UserService = inject(UserService);
  const msalBroadcastService = inject(MsalBroadcastService);

  const hasMsalHash =
    window.location.hash.includes("code=") ||
    window.location.hash.includes("id_token=") ||
    window.location.hash.includes("error=");

  // Core auth check: verify AEM token and load staff info if needed.
  const checkAuth$ = inject(AuthService)
    .check()
    .pipe(
      switchMap((authenticated) => {
        if (!authenticated) {
          return of(router.createUrlTree(["/login"]));
        }

        // User already loaded — allow immediately
        if (userService.user()) {
          return of(true);
        }

        // Fetch staff info for the first time
        return userService.getStaffInfo().pipe(
          map((user) => {
            if (user) return true;
            // Token was cleared during getStaffInfo (e.g. 401)
            const token = localStorage.getItem("aemAccessToken");
            return token
              ? router.createUrlTree(["/error-500"])
              : router.createUrlTree(["/login"]);
          }),
          catchError(() => {
            const token = localStorage.getItem("aemAccessToken");
            return of(token
              ? router.createUrlTree(["/error-500"])
              : router.createUrlTree(["/login"])
            );
          })
        );
      })
    );

  // On mobile, MSAL uses redirect flow. The browser returns to the app with a
  // hash (#code=...). AppComponent.handleRedirectObservable() processes it and
  // calls signIn() to obtain the AEM token. We must wait for that to complete
  // before running checkAuth$ — otherwise the token won't exist yet.
  if (hasMsalHash) {
    return msalBroadcastService.inProgress$.pipe(
      // Wait for MSAL to finish processing the redirect
      filter((status) => status === InteractionStatus.None),
      take(1),
      switchMap(() =>
        // Poll every 100ms until the AEM token is written by AppComponent.getToken()
        // or until loading is done (meaning signIn failed/was skipped)
        interval(100).pipe(
          map(() => {
            const token = localStorage.getItem("aemAccessToken");
            return {
              hasValidToken: !!token && !AuthUtils.isTokenExpired(token),
              isLoading: userService.isLoading(),
            };
          }),
          filter((s) => s.hasValidToken || !s.isLoading),
          take(1),
          switchMap((s) =>
            s.hasValidToken
              ? checkAuth$
              : of(router.createUrlTree(["/login"]))
          )
        )
      )
    );
  }

  return checkAuth$;
};
